import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Stack } from '@mui/material';

import pusherJs from 'pusher-js';

import { useAppContext } from '../app/Context';
import Spinner from '../components/Spinner';
import { createPusher } from './helpers';

interface PusherContextProps {
  pusherRef: MutableRefObject<pusherJs | null>;
}

const PusherContext = createContext<PusherContextProps | undefined>(undefined);

export function usePusherContext() {
  const context = useContext(PusherContext);
  if (!context) {
    throw new Error('Using usePusherContext outside PusherContextProvider is not allowed.');
  }
  return context;
}

function usePusherContextProviderVM() {
  const { session } = useAppContext();
  const pusherRef = useRef<pusherJs | null>(null);
  const [isConnectingPusher, setIsConnectingPusher] = useState<boolean>(true);

  const createPusherFn = useCallback(async () => {
    setIsConnectingPusher(true);

    if (!session?.socket?.cluster) {
      setIsConnectingPusher(false);
      return;
    }
    if (!session.socket.key) {
      setIsConnectingPusher(false);
      return;
    }

    const pusher = await createPusher({
      sessionToken: session.token,
      sessionCluster: session.cluster,
      socketCluster: session.socket.cluster,
      socketKey: session.socket.key,
    });

    pusher?.connection.bind('connected', () => {
      pusherRef.current = pusher;
      setIsConnectingPusher(false);
    });
  }, [session?.token, session?.cluster, session?.socket?.cluster, session?.socket?.key]);

  useEffect(() => {
    createPusherFn();
    return function cleanup() {
      pusherRef.current?.disconnect();
    };
  }, [createPusherFn]);

  return useMemo(
    () => ({
      pusherRef,
      isConnectingPusher,
    }),
    [isConnectingPusher]
  );
}

export interface PusherContextProviderProps {
  children: ReactNode;
}

export function PusherContextProvider({ children }: PusherContextProviderProps) {
  const { isConnectingPusher, ...providerValue } = usePusherContextProviderVM();

  if (isConnectingPusher) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center">
        <Spinner size="xLarge" />
      </Stack>
    );
  }

  return <PusherContext.Provider value={providerValue}>{children}</PusherContext.Provider>;
}
